import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	GeneralAction,
	NavBreadcrumb,
	PageRequest,
	ServerDataResponse,
	TableHeaderItem,
	TemplatesWithOptions
} from '@models/models';
import { Observable, Subject } from 'rxjs';
import { SNModalService } from '@web-services/modal/snModal.service';
import { ManageLeadSourceModalComponent } from '@web-components/settings/lead-sources-section/modals/manage-lead-source-modal/manage-lead-source-modal.component';
import { MessageService } from '@services/helpers/message-service/message.service';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { DeleteLeadSourceModalComponent } from '@web-components/settings/lead-sources-section/modals/delete-lead-source-modal/delete-lead-source-modal.component';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { LeadSourcesDataService } from '@services/lead-sources/lead-sources.data.service';
import {
	DeleteLeadSourceMetadata,
	LeadSourceColor,
	LeadSourceItem,
	LeadSourcesTemplates
} from '@models/lead-sources.model';
import { of } from 'rxjs/observable/of';

@Component({
	selector: 'lead-sources-section',
	template: require('./lead-sources-section.html'),
	styles: [require('./lead-sources-section.scss')]
})
export class LeadSourcesSectionComponent implements OnInit, OnDestroy {
	readonly breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: null, title: 'Lead Sources' }
	];

	readonly tableHeader: Array<TableHeaderItem> = [
		{ text: 'Source name', isSortable: true, sortingCriteria: 'name' },
		{ text: 'Colour', isSortable: false, sortingCriteria: null },
		{ text: '', isSortable: false, sortingCriteria: null }
	];

	pageRequestActive: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 10,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	pageRequestArchived: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 5,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	activeTemplates: TemplatesWithOptions<LeadSourceItem>;
	archivedTemplates: TemplatesWithOptions<LeadSourceItem>;
	availableColours: Array<LeadSourceColor>;
	leadSourcesData: LeadSourcesTemplates;
	addNewAction: GeneralAction;
	buttonDisable: boolean;

	private companyId: number;
	private unsubscribe: Subject<void> = new Subject<void>();

	constructor(
		private modalService: SNModalService,
		private leadSourcesService: LeadSourcesDataService,
		private messageService: MessageService,
		private accountService: AccountServiceWebProvider
	) {}

	ngOnInit(): void {
		this.getCompanyId();
		this.applyPageProductFromLocalStorage('ACTIVE', this.pageRequestActive);
		this.applyPageProductFromLocalStorage('ARCHIVED', this.pageRequestArchived);
		this.getLeadSourcesList('ACTIVE', this.pageRequestActive);
		this.getLeadSourcesList('ARCHIVED', this.pageRequestArchived);
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	openEditModal(
		action: string,
		pageRequest: PageRequest,
		item?: LeadSourceItem
	): void {
		const props =
			action === 'CREATE_NEW' ? this.addNewAction : item.actions.EDIT;

		if (this.checkForAlertModalMethod(props)) {
			return;
		}

		const modal = this.modalService.openModal(ManageLeadSourceModalComponent, {
			windowClass: 'ng-bootstrap-modal modal-visible'
		});
		modal.componentInstance.action = { action, props };
		modal.componentInstance.colours = this.availableColours;
		if (item) {
			modal.componentInstance.selectedItem = item;
		}

		modal.result
			.then((state: string): void => {
				if (state === 'CREATE_NEW') {
					this.saveNewLeadSource();
				}

				if (state === 'EDIT_EXISTING') {
					this.editLeadSourceItem(item.status, pageRequest);
				}
			})
			.catch();
	}

	archiveLeadSource(item: LeadSourceItem): void {
		if (this.checkForAlertModalMethod(item.actions.ARCHIVE)) {
			return;
		}

		const { url, method } = item.actions.ARCHIVE;
		this.buttonDisable = true;
		this.archiveOrUnarchiveLeadSource(url, method).subscribe((): void => {
			this.messageService.showToaster('s_2', 'leadSources');
		});
	}

	unarchiveLeadSource(item: LeadSourceItem): void {
		if (this.checkForAlertModalMethod(item.actions.UNARCHIVE)) {
			return;
		}

		const { url, method } = item.actions.UNARCHIVE;
		this.archiveOrUnarchiveLeadSource(url, method).subscribe((): void => {
			this.messageService.showToaster('s_4', 'leadSources');
		});
	}

	updateTemplateList(
		data: PageRequest,
		status: string,
		pageRequestType: string
	): void {
		this[pageRequestType] = JSON.parse(JSON.stringify(data));

		this.getLeadSourcesList(status, this[pageRequestType]);
	}

	deleteLeadSource(item: LeadSourceItem, pageRequest: PageRequest): void {
		if (this.checkForAlertModalMethod(item.actions.DELETE)) {
			return;
		}
		this.buttonDisable = true;
		const { url, method } = item.actions.DELETE;
		this.leadSourcesService
			.sendAction(url, method, {})
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.buttonDisable = false))
			)
			.subscribe(
				(data: ServerDataResponse<DeleteLeadSourceMetadata>): void => {
					const metadata: DeleteLeadSourceMetadata = data.data;
					const modal = this.modalService.openModal(
						DeleteLeadSourceModalComponent,
						{ windowClass: 'ng-bootstrap-modal modal-visible modal-short' }
					);
					modal.componentInstance.action = metadata.actions.MERGE_AND_DELETE;
					modal.componentInstance.leadSource = item;
					modal.componentInstance.leadSourcesList = metadata.leadSources;
					modal.componentInstance.linkedEvents = metadata.totalLinkedEvents;

					modal.result
						.then((): void => {
							this.messageService.showToaster('s_3', 'leadSources');
							pageRequest.paging.pageNumber = 0;
							pageRequest.paging.searchToken = null;
							this.getLeadSourcesList(item.status, pageRequest);
						})
						.catch();
				},
				(err): void => this.handleError(err)
			);
	}

	private archiveOrUnarchiveLeadSource(
		url: string,
		method: string
	): Observable<void> {
		this.buttonDisable = true;
		return this.leadSourcesService.sendAction(url, method, {}).pipe(
			takeUntil(this.unsubscribe),
			finalize((): boolean => (this.buttonDisable = false)),
			catchError((err): Observable<boolean> => {
				this.handleError(err);
				return Observable.empty();
			}),
			switchMap((): Observable<void> => {
				this.pageRequestActive.paging.searchToken = null;
				this.pageRequestActive.paging.pageNumber = 0;
				this.pageRequestArchived.paging.searchToken = null;
				this.pageRequestArchived.paging.pageNumber = 0;

				this.getLeadSourcesList('ACTIVE', this.pageRequestActive);
				this.getLeadSourcesList('ARCHIVED', this.pageRequestArchived);
				return of(null);
			})
		);
	}

	private getLeadSourcesList(status: string, pageRequest: PageRequest): void {
		this.leadSourcesService
			.getLeadSourcesOverview(status, pageRequest)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				({ data }): void =>
					this.successGetLeadSourcesTemplates(data, status, pageRequest),
				(err): void => this.handleError(err)
			);
	}

	private successGetLeadSourcesTemplates(
		data: LeadSourcesTemplates,
		status: string,
		pageRequest: PageRequest
	): void {
		if (status === 'ACTIVE' && data.actions.ADD_NEW_ONE) {
			this.addNewAction = data.actions.ADD_NEW_ONE;
		}
		this[`${status.toLowerCase()}Templates`] = data.pagingResult;
		this.availableColours = data.colours;
		pageRequest.paging.searchToken = data.pagingResult.searchToken;
		this.setPageProductToLocalStorage(status, pageRequest);
	}

	private saveNewLeadSource(): void {
		this.pageRequestActive.paging.pageNumber = 0;
		this.pageRequestActive.paging.searchToken = null;
		this.getLeadSourcesList('ACTIVE', this.pageRequestActive);
		this.messageService.showToaster('s_0', 'leadSources');
	}

	private editLeadSourceItem(status: string, pageRequest: PageRequest): void {
		this.getLeadSourcesList(status, pageRequest);
		this.messageService.showToaster('s_1', 'leadSources');
	}

	private checkForAlertModalMethod(action: GeneralAction): boolean {
		const { method, alertMessage } = action;

		if (method === 'ALERT_MODAL') {
			const { title, message } = alertMessage;
			this.openAlertModal(title, message);
			return true;
		}

		return false;
	}

	private openAlertModal(title, text): void {
		const alertModal = this.modalService.openModal(
			ConfirmGeneralModalComponent
		);
		alertModal.componentInstance.title = title;
		alertModal.componentInstance.text = text;

		alertModal.componentInstance.buttons = [
			{
				text: 'Got it',
				color: 'default',
				size: 'lg',
				action: 'CLOSE_ALERT'
			}
		];
	}

	private applyPageProductFromLocalStorage(
		status: string,
		pageRequest: PageRequest
	): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`leadSourcesRequest${status}_${this.companyId}`)
		);

		if (pageRequestObject && pageRequestObject !== '') {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			pageRequest.paging.pageSize = pageSize;
			pageRequest.sortCriterias[0].fieldName = fieldName;
			pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPageProductToLocalStorage(
		status: string,
		pageRequest: PageRequest
	): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`leadSourcesRequest${status}_${this.companyId}`);
		localStorage.setItem(
			`leadSourcesRequest${status}_${this.companyId}`,
			pageRequestString
		);
	}

	private getCompanyId(): void {
		this.accountService.companyIdObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: number): number => (this.companyId = data));
	}

	private handleError({ error }): void {
		const { errorMessage, errorTitle } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/lead-sources-section/lead-sources-section.ts